import { Gift, Layers, Percent, Repeat, RefreshCw, Ticket, type LucideIcon } from "lucide-react";
import type { CampaignType } from "../../lib/types";

/** Which editor the reward step renders. A new one is added here, not inline. */
export type CampaignBuilder = "tiers" | "rules" | "discounts" | "subscriptions" | "code";

export interface CampaignTypeDefinition {
	type: CampaignType;
	group: string;
	label: string;
	description: string;
	icon: LucideIcon;
	builder: CampaignBuilder;
	rewardStep: { label: string; description: string };
	/**
	 * Campaign settings this type actually uses. Anything false is hidden rather
	 * than shown and ignored, which is how a stacking control ended up on a
	 * campaign with one tier.
	 */
	settings: {
		stacking: boolean;
		roundUp: boolean;
		combinesWith: boolean;
	};
	/** `tiers` builder only: one tier, created with the campaign. */
	singleTier?: boolean;
}

const SPEND_SETTINGS = { stacking: true, roundUp: true, combinesWith: true };

export const CAMPAIGN_TYPES: readonly CampaignTypeDefinition[] = [
	{
		type: "simple_free_gift",
		group: "Free gifts",
		label: "Simple free gift",
		description: "One gift, optionally behind a minimum spend.",
		icon: Gift,
		builder: "tiers",
		singleTier: true,
		rewardStep: { label: "Gift", description: "What shoppers earn" },
		settings: { ...SPEND_SETTINGS, stacking: false },
	},
	{
		type: "tiered_free_gifts",
		group: "Free gifts",
		label: "Tiered free gifts",
		description: "Several spend thresholds, each unlocking its own gift.",
		icon: Layers,
		builder: "tiers",
		rewardStep: { label: "Tiers & gifts", description: "What shoppers earn" },
		settings: SPEND_SETTINGS,
	},
	{
		type: "buy_x_get_y",
		group: "Buy X get Y",
		label: "Buy X get Y",
		description: "Buying one thing earns another free or discounted.",
		icon: Repeat,
		builder: "rules",
		rewardStep: { label: "Offers", description: "Buy this, get that" },
		settings: { stacking: false, roundUp: false, combinesWith: true },
	},
	{
		type: "subscription_upsell",
		group: "Subscriptions",
		label: "Subscription upsell",
		description: "Ask shoppers to switch a cart item onto a subscription.",
		icon: RefreshCw,
		builder: "subscriptions",
		rewardStep: { label: "Offers", description: "What to pitch, and when" },
		settings: { stacking: false, roundUp: false, combinesWith: true },
	},
	{
		type: "product_discount",
		group: "Discounts",
		label: "Percentage or amount off",
		description: "Mark down products, a product type, a vendor or a tag.",
		icon: Percent,
		builder: "discounts",
		rewardStep: { label: "Discounts", description: "What gets marked down" },
		settings: { stacking: false, roundUp: false, combinesWith: true },
	},
	{
		type: "threshold_code",
		group: "Discounts",
		label: "Unlockable discount code",
		description: "Reveal one of your Shopify discount codes once the cart reaches a spend.",
		icon: Ticket,
		builder: "code",
		rewardStep: { label: "Code", description: "The code, and what unlocks it" },
		// The code's own combination settings in Shopify govern, so a control here
		// would do nothing.
		settings: { stacking: false, roundUp: true, combinesWith: false },
	},
];

export const DEFAULT_CAMPAIGN_TYPE: CampaignType = "simple_free_gift";

export const campaignTypeFor = (type: CampaignType): CampaignTypeDefinition | undefined =>
	CAMPAIGN_TYPES.find((entry) => entry.type === type);

export const campaignTypeGroups = (): {
	group: string;
	types: CampaignTypeDefinition[];
}[] => {
	const groups: { group: string; types: CampaignTypeDefinition[] }[] = [];

	for (const type of CAMPAIGN_TYPES) {
		const existing = groups.find((entry) => entry.group === type.group);
		if (existing) existing.types.push(type);
		else groups.push({ group: type.group, types: [type] });
	}

	return groups;
};
